import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { apiFetch } from '@/lib/api';

/** `POST /api/licenses/:id/devices/:activationId/deactivate` — release one seat. */
async function deactivateSeat(
  licenseId: string,
  activationId: string,
  reason: string | null
): Promise<void> {
  await apiFetch(
    `/licenses/${encodeURIComponent(licenseId)}/devices/${encodeURIComponent(activationId)}/deactivate`,
    { method: 'POST', body: JSON.stringify(reason ? { reason } : {}) }
  );
}

interface DeactivateDeviceDialogProps {
  /** Target seat (`activations.id`); `null` keeps the dialog closed. */
  activationId: string | null;
  /** Licence the seat belongs to — the device list refreshed on success. */
  licenseId: string;
  onOpenChange: (open: boolean) => void;
}

/**
 * Server-side release of one device seat. Confirming frees the licence slot (the
 * seat row is soft-deactivated, the history is kept) so the robot's next activation
 * can reclaim it by presenting its own fingerprint — the client is never touched.
 */
export default function DeactivateDeviceDialog({
  activationId,
  licenseId,
  onOpenChange,
}: DeactivateDeviceDialogProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');

  const mutation = useMutation({
    mutationFn: () =>
      deactivateSeat(licenseId, activationId!, reason.trim() === '' ? null : reason.trim()),
    onSuccess: () => {
      toast.success(t('licenses.detail.devices.deactivate.toastSuccess'));
      queryClient.invalidateQueries({ queryKey: ['license', 'devices', licenseId] });
      queryClient.invalidateQueries({ queryKey: ['license', 'detail', licenseId] });
      queryClient.invalidateQueries({ queryKey: ['license', 'audit', licenseId] });
      setReason('');
      onOpenChange(false);
    },
    onError: () => {
      toast.error(t('licenses.detail.devices.deactivate.toastError'));
    },
  });

  const handleClose = (open: boolean) => {
    if (!open && mutation.isPending) return;
    if (!open) setReason('');
    onOpenChange(open);
  };

  return (
    <Dialog open={activationId !== null} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('licenses.detail.devices.deactivate.title')}</DialogTitle>
          <DialogDescription>
            {t('licenses.detail.devices.deactivate.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Label htmlFor="deactivate-reason">
            {t('licenses.detail.devices.deactivate.reason')}
          </Label>
          <Input
            id="deactivate-reason"
            autoComplete="off"
            maxLength={512}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            data-testid="deactivate-reason"
          />
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => handleClose(false)}
            disabled={mutation.isPending}
          >
            {t('licenses.detail.devices.deactivate.cancel')}
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
            data-testid="deactivate-confirm"
          >
            {mutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('licenses.detail.devices.deactivate.deactivating')}
              </>
            ) : (
              t('licenses.detail.devices.deactivate.confirm')
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
